import { useEffect, useState } from 'react';

import { createPreviewSession, createPreviewUrls } from '@api/instant_survey';
import { isEUDomain, isLocalSurveyDomain } from '@helpers';
import { toast, useResetControllerRef } from '@utilities';
import type { ErrorProps } from '@utilities/components/Error';

import { type PreviewSelection } from './helpers';

type ErrorStatus = ErrorProps['status'];

/**
 * modelled after the useQuery hook in tanstack/query,
 * this hook makes use of the api for creating preview sessions and returns an object
 * to track state for the data from the api, loading said data and any potential errors we encounter.
 * For our use case the data returned is the id for a preview session based off our selected
 * question id and quota group id
 */
export const useIframeUrl = ({
  instantSurveyId,
  previewSelection,
}: {
  instantSurveyId: number;
  previewSelection: PreviewSelection;
}) => {
  const { questionId, quotaGroupId, restartKey } = previewSelection;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorStatus>();
  const [iframeUrl, setIframeUrl] = useState<null | string>(null);
  const { clearIfCurrent, resetController } = useResetControllerRef();

  useEffect(() => {
    if (questionId === undefined || quotaGroupId === undefined) {
      setError(undefined);
      setIframeUrl(null);
      setIsLoading(false);
      return;
    }

    const getPreviewSession = async () => {
      const controller = resetController();
      setError(undefined);
      setIsLoading(true);

      try {
        const previewUrl = await getIframeUrl({
          instantSurveyId,
          previewSelection: { questionId, quotaGroupId },
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;
        setIframeUrl(previewUrl);
      } catch (sessionError) {
        if (controller.signal.aborted) return;
        showError({
          error: sessionError,
          message: 'Error creating survey preview',
          setError,
        });
      } finally {
        if (!controller.signal.aborted) {
          clearIfCurrent(controller);
          setIsLoading(false);
        }
      }
    };

    getPreviewSession();
    // restartKey is intentionally included so Restart creates a fresh preview
    // session even when questionId and quotaGroupId are unchanged.
  }, [clearIfCurrent, instantSurveyId, questionId, quotaGroupId, resetController, restartKey]);

  return { error, iframeUrl, isLoading };
};

/**
 * modelled after the useQuery hook in tanstack/query,
 * this hook makes use of the api for creating preview sessions and returns an object
 * to track state for the data from the api, loading said data and any potential errors we encounter.
 * For our use case the data returned ends up being the preview url for a specific quota group
 */
export const usePublicLink = ({
  instantSurveyId,
  quotaGroupId,
}: {
  instantSurveyId: number;
  quotaGroupId: number | string | undefined;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorStatus>();
  const [publicLink, setPublicLink] = useState('');
  const { clearIfCurrent, resetController } = useResetControllerRef();

  useEffect(() => {
    if (quotaGroupId == null) {
      setError(undefined);
      setPublicLink('');
      setIsLoading(false);
      return;
    }

    const getShareablePublicLink = async () => {
      const controller = resetController();
      setError(undefined);
      setIsLoading(true);

      try {
        const previewUrl = await getPublicLink({
          id: instantSurveyId,
          quotaGroupId,
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;
        setPublicLink(previewUrl);
      } catch (error) {
        if (controller.signal.aborted) return;
        showError({
          error,
          message: 'Error creating shareable survey preview',
          setError,
        });
        setPublicLink('');
      } finally {
        if (!controller.signal.aborted) {
          clearIfCurrent(controller);
          setIsLoading(false);
        }
      }
    };

    getShareablePublicLink();
  }, [clearIfCurrent, instantSurveyId, quotaGroupId, resetController]);

  return { error, isLoading, publicLink };
};

const getIframeUrl = async ({
  instantSurveyId,
  previewSelection: { questionId, quotaGroupId },
  signal,
}: {
  instantSurveyId: number;
  previewSelection: PreviewSelection;
  signal: AbortSignal;
}) => {
  if (questionId == null || quotaGroupId == null) throw new Error();
  const data = await createPreviewSession({
    instantSurveyId,
    previewSelection: { quotaGroupId, startingQuestionId: questionId },
    signal: signal,
  });

  const previewEndpoint = import.meta.env.VITE_SURVEY_PREVIEW_ENDPOINT;
  if (!previewEndpoint) throw new Error('Survey preview endpoint is not defined');
  const previewEndpointUrl = new URL(previewEndpoint);

  if (!data?.id) throw new Error('Preview Session not found');
  previewEndpointUrl.searchParams.set('preview_session_id', data?.id);

  const infraRegion = getPreviewInfraRegion();
  if (infraRegion) previewEndpointUrl.searchParams.set('infra_region', infraRegion);

  return previewEndpointUrl.toString();
};

const getPublicLink = async ({
  id,
  quotaGroupId,
  signal,
}: {
  id: number;
  quotaGroupId: number | string | undefined;
  signal: AbortSignal;
}) => {
  const previewUrls = await createPreviewUrls({
    instantSurveyId: id,
    signal,
  });

  if (signal.aborted) return '';

  const previewUrlForQuotaGroup = previewUrls.find(
    (previewUrl) => String(previewUrl.quotaGroupId) === String(quotaGroupId)
  );

  if (!previewUrlForQuotaGroup?.previewUrl) {
    throw new Error('No preview URL found for the selected quota group');
  }

  const previewEndpoint = import.meta.env.VITE_SURVEY_PREVIEW_ENDPOINT;
  if (!previewEndpoint) throw new Error('Survey preview endpoint is not defined');
  const publicLink = new URL(previewUrlForQuotaGroup.previewUrl, previewEndpoint);

  const infraRegion = getPreviewInfraRegion();
  if (infraRegion) publicLink.searchParams.set('infra_region', infraRegion);

  return publicLink.toString();
};

const showError = ({
  error,
  message,
  setError,
}: {
  error: unknown;
  message: string;
  setError: (value: React.SetStateAction<number | string | undefined>) => void;
}) => {
  toast.error(`${message}. Please try again.`);
  console.error(`${message}: ${error}`);
  setError((error as { response?: { status?: ErrorProps['status'] } })?.response?.status);
};

export const getPreviewInfraRegion = () => {
  if (isLocalSurveyDomain) return '';
  return isEUDomain() ? 'EU' : 'NA';
};
